import React from "react";
import styled from "styled-components";
import { LuArrowUpRight, LuPlus } from "react-icons/lu";

import StatusChip from "../StatusChip";

const Media = styled.div`
  position: absolute;
  inset: 0;
  background-color: var(--card);
  background-image: ${(props) => (props.$img ? `url(${props.$img})` : "none")};
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  transition: transform 0.5s;
`;

const Wrapper = styled.article`
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid var(--border);
  border-radius: 14px;
  outline: 1px solid ${(props) => (props.$highlighted ? "var(--purple)" : "transparent")};
  outline-offset: -1px;
  background: var(--card);
  overflow: hidden;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  transition: transform 0.25s, outline-color 0.25s, box-shadow 0.25s;
  &:hover {
    transform: translateY(-4px);
    outline-color: ${(props) => (props.$highlighted ? "var(--purple)" : "var(--borderStrong)")};
    box-shadow: 0 18px 44px rgba(0, 0, 0, 0.4);
    ${Media} {
      transform: scale(1.04);
    }
  }
`;

const MediaBox = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-shrink: 0;
  height: 205px;
  padding: 16px;
  border-bottom: 1px dashed var(--borderStrong);
  overflow: hidden;
  > * {
    z-index: 1;
  }
  > ${Media} {
    z-index: 0;
  }
`;

const OpenProject = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(8, 10, 16, 0.72);
  color: var(--text);
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: var(--lime);
    color: var(--ink);
  }
`;

const Information = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  padding: 20px 22px 22px;
`;

const Identity = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  h3 {
    color: var(--text);
    font-family: var(--fontHeading);
    font-size: 28px;
    font-weight: 700;
  }
  span {
    flex-shrink: 0;
    color: var(--textDim);
    font-family: var(--fontMono);
    font-size: 9px;
  }
`;

const Description = styled.p`
  color: var(--textMuted);
  font-family: var(--fontBody);
  font-size: 14px;
  line-height: 1.48;
`;

const DetailsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  margin-top: auto;
  padding-top: 8px;
  border: none;
  background: transparent;
  color: var(--lime);
  font-family: var(--fontMono);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  svg {
    transition: transform 0.25s;
  }
  ${Wrapper}:hover & svg {
    transform: rotate(90deg);
  }
  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 4px;
  }
`;

function ProjectCard(props) {
  return (
    <Wrapper
      $highlighted={props.highlighted}
      $clickable={Boolean(props.onOpen)}
      onClick={props.onOpen}
    >
      <MediaBox>
        <Media $img={props.img} />
        <StatusChip $variant={props.chipVariant}>{props.tag}</StatusChip>
        {props.url && (
          <OpenProject
            href={`https://${props.url}/`}
            target={"_blank"}
            rel={"noreferrer"}
            aria-label={`Open ${props.title}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <LuArrowUpRight size={16} />
          </OpenProject>
        )}
      </MediaBox>
      <Information>
        <Identity>
          <h3>{props.title}</h3>
          <span>{props.code}</span>
        </Identity>
        <Description>{props.shortDesc}</Description>
        {props.onOpen && (
          <DetailsButton
            onClick={(e) => {
              e.stopPropagation();
              props.onOpen();
            }}
          >
            View details
            <LuPlus size={12} />
          </DetailsButton>
        )}
      </Information>
    </Wrapper>
  );
}

export default ProjectCard;
